import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authApi, superadminApi } from '../lib/api';
import type { TenantOnboardingDetail } from '../lib/api';
import type { CurrentUser } from '../types';
import AppShell from '../components/AppShell';
import UploadVendorDataModal from '../components/UploadVendorDataModal';
import PromoteToCustomerModal from '../components/PromoteToCustomerModal';

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  canvas:       '#f8fafc',
  surface:      '#ffffff',
  border:       '#e2e8f0',
  borderSoft:   '#eef2f7',
  primary:      '#0ea5e9',
  primarySoft:  '#e0f2fe',
  textPrimary:  '#0f172a',
  textSecondary:'#475569',
  textMuted:    '#94a3b8',
  green:        '#10b981',
  greenSoft:    '#d1fae5',
} as const;

const SANS = "'Roboto', system-ui, sans-serif";
const MONO = "'Courier New', monospace";

// ── Stage config ─────────────────────────────────────────────────────────────
type StageKey = 'excelSent' | 'dataUploaded' | 'benchmarked' | 'proposalSent' | 'poReceived' | 'activating' | 'live';

const STAGES: Array<{ n: number; key: StageKey; label: string; description: string }> = [
  { n: 1, key: 'excelSent',    label: 'Excel Sent',     description: 'Onboarding spreadsheet sent to the portal admin. Awaiting completed vendor data.' },
  { n: 2, key: 'dataUploaded', label: 'Data Uploaded',  description: 'Vendor data ingested from the spreadsheet. FX conversion applied; ready for benchmarking.' },
  { n: 3, key: 'benchmarked',  label: 'Benchmarked',    description: 'Trigger Claude benchmarking to identify savings opportunities across the imported vendors.' },
  { n: 4, key: 'proposalSent', label: 'Proposal Sent',  description: 'Savings proposal delivered to the client. Awaiting acceptance or counter-terms.' },
  { n: 5, key: 'poReceived',   label: 'PO Received',    description: 'Purchase order confirmed. Preparing production tenant activation.' },
  { n: 6, key: 'activating',   label: 'Activating',     description: 'SSO provisioning, user invites, and production data migration in progress.' },
  { n: 7, key: 'live',         label: 'Live',           description: 'Tenant is fully operational. Ongoing support and benchmarking refreshes.' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtMoneyUsd(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}

function humanAction(a: string): string {
  if (a === 'tenant.stage_advanced')    return 'Stage advanced';
  if (a === 'tenant.stage_reverted')    return 'Stage reverted';
  if (a === 'tenant.benchmarking_run')  return 'Benchmarking run started';
  if (a === 'tenant.proposal_sent')     return 'Proposal sent';
  if (a === 'tenant.po_received')       return 'PO received';
  if (a === 'delete')                   return 'Tenant offboarded';
  return a.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function TenantOnboardingPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate     = useNavigate();
  const [user, setUser]       = useState<CurrentUser | null>(null);
  const [detail, setDetail]   = useState<TenantOnboardingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<'not_found' | 'network' | null>(null);
  const [tab, setTab]         = useState<'info' | 'audit' | 'vendors'>('info');
  const [toast, setToast]     = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const showToast = (kind: 'success' | 'error', message: string) => {
    setToast({ kind, message });
    window.setTimeout(() => setToast((t) => (t && t.message === message ? null : t)), 5000);
  };

  const refreshDetail = async () => {
    if (!tenantId) return;
    try {
      const d = await superadminApi.onboardingDetail(tenantId);
      setDetail(d);
    } catch {
      /* swallow — keep existing detail on refresh failure */
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    authApi.me()
      .then((u) => {
        if (u.role !== 'super_admin') { navigate('/dashboard', { replace: true }); return; }
        setUser(u);
        return superadminApi.onboardingDetail(tenantId);
      })
      .then((d) => { if (d) setDetail(d); })
      .catch((err: Error) => {
        if (/not\s*found|404/i.test(err.message)) setError('not_found');
        else setError('network');
      })
      .finally(() => setLoading(false));
  }, [tenantId, navigate]);

  function retry() {
    if (!tenantId) return;
    setError(null); setLoading(true);
    superadminApi.onboardingDetail(tenantId)
      .then(setDetail)
      .catch((err: Error) => {
        if (/not\s*found|404/i.test(err.message)) setError('not_found');
        else setError('network');
      })
      .finally(() => setLoading(false));
  }

  if (!user) return null;

  return (
    <AppShell user={user} title="Tenant Onboarding" subtitle={detail?.tenant.name ?? ''}>
      <style>{PAGE_CSS}</style>

      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {/* Topbar — back link */}
        <div style={{ marginBottom: 18 }}>
          <Link to="/superadmin" style={backLinkStyle}>
            <span style={{ fontSize: 13, lineHeight: 1 }}>←</span>
            <span>Back to pipeline</span>
          </Link>
        </div>

        {loading && <PageSkeleton />}

        {!loading && error === 'not_found' && (
          <ErrorState
            title="Tenant not found"
            message="This tenant either doesn't exist or has been offboarded."
            action={<Link to="/superadmin" style={primaryBtnStyle}>Return to pipeline</Link>}
          />
        )}

        {!loading && error === 'network' && (
          <ErrorState
            title="Couldn't load tenant data"
            message="The server didn't respond. This is usually a temporary connection issue."
            action={<button onClick={retry} style={primaryBtnStyle}>Retry</button>}
          />
        )}

        {!loading && !error && detail && (
          <OnboardingContent
            detail={detail}
            tab={tab}
            setTab={setTab}
            onToast={showToast}
            onRefresh={refreshDetail}
            onDeleted={() => navigate('/superadmin')}
          />
        )}
      </div>

      {toast && <Toast kind={toast.kind} message={toast.message} onClose={() => setToast(null)} />}
    </AppShell>
  );
}

function Toast({ kind, message, onClose }: { kind: 'success' | 'error'; message: string; onClose: () => void }) {
  const bg     = kind === 'success' ? '#d1fae5' : '#fee2e2';
  const border = kind === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)';
  const color  = kind === 'success' ? '#065f46' : '#991b1b';
  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 300,
      background: bg, border: `1px solid ${border}`, color,
      padding: '12px 16px', borderRadius: 10, fontSize: 13,
      boxShadow: '0 6px 18px rgba(0,0,0,0.10)', maxWidth: 360,
      display: 'flex', gap: 12, alignItems: 'flex-start',
      animation: 'sl-fade 0.25s ease-out',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}

// ── Content ──────────────────────────────────────────────────────────────────
type ToastFn = (kind: 'success' | 'error', message: string) => void;

function OnboardingContent({
  detail, tab, setTab, onToast, onRefresh, onDeleted,
}: {
  detail: TenantOnboardingDetail;
  tab: 'info' | 'audit' | 'vendors';
  setTab: (t: 'info' | 'audit' | 'vendors') => void;
  onToast: ToastFn;
  onRefresh: () => Promise<void>;
  onDeleted: () => void;
}) {
  const { tenant, stageData, auditLog } = detail;
  // onboardingStage is the *highest stage completed*, NOT the current stage.
  // Current action = stage N+1 (or stage 7 "Live" when all stages are complete).
  const completedN   = Math.min(Math.max(tenant.onboardingStage, 0), 7);
  const isLive       = completedN >= 7;
  const currentStageN = isLive ? 7 : completedN + 1;
  const currentStage = STAGES.find((s) => s.n === currentStageN) ?? STAGES[0];
  const completed    = STAGES.filter((s) => (isLive ? s.n < 7 : s.n <= completedN));
  const upcoming     = STAGES.filter((s) => s.n > currentStageN);

  return (
    <>
      {/* Title block */}
      <div className="sl-fade" style={{ marginBottom: 28 }}>
        <h1 style={pageTitleStyle}>{tenant.name}</h1>
        <div style={subInfoRowStyle}>
          <span style={{ fontFamily: MONO, fontSize: 12.5 }}>{tenant.emailDomain ?? '—'}</span>
          <InfoDot />
          <span style={chipStyle}>{tenant.displayCurrency}</span>
          <InfoDot />
          <span style={chipStyle}>{tenant.region}</span>
          {tenant.hqCountry && (
            <>
              <InfoDot />
              <span style={{ fontSize: 12.5, color: C.textSecondary }}>{tenant.hqCountry}</span>
            </>
          )}
          <InfoDot />
          <span style={{ fontSize: 12.5, color: C.textMuted }}>
            Created {fmtDate(tenant.createdAt)}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="sl-fade" style={{ ...cardStyle, padding: '28px 32px 22px', marginBottom: 24 }}>
        <StageProgressBar completedStage={completedN} stageData={stageData} />
      </div>

      {/* Current stage card */}
      <div className="sl-fade" style={{ marginBottom: 32 }}>
        <CurrentStageCard
          stage={currentStage}
          stageData={stageData}
          tenantId={tenant.id}
          tenantName={tenant.name}
          customerStatus={tenant.customerStatus ?? null}
          onToast={onToast}
          onRefresh={onRefresh}
        />
      </div>

      {/* Completed stages */}
      {completed.length > 0 && (
        <section className="sl-fade" style={{ marginBottom: 32 }}>
          <SectionLabel>Completed</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {completed.map((s, i) => (
              <CompletedStageCard
                key={s.key}
                stage={s}
                stageData={stageData}
                animationDelay={i * 40}
                tenantId={tenant.id}
                onToast={onToast}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </section>
      )}

      {/* Future stages */}
      {upcoming.length > 0 && (
        <section className="sl-fade" style={{ marginBottom: 32 }}>
          <SectionLabel>Upcoming</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {upcoming.map((s) => (
              <div key={s.key} style={upcomingRowStyle}>
                <span style={{ fontSize: 11, color: C.textMuted, fontFamily: MONO, width: 24 }}>
                  {String(s.n).padStart(2, '0')}
                </span>
                <span style={{ fontSize: 13.5, color: C.textMuted, fontWeight: 300 }}>{s.label}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className="sl-fade" style={{ marginBottom: 48 }}>
        <div style={tabsBarStyle}>
          <TabBtn active={tab === 'info'}    onClick={() => setTab('info')}>Tenant Info</TabBtn>
          <TabBtn active={tab === 'audit'}   onClick={() => setTab('audit')}>
            Audit Log <span style={tabCountStyle}>{auditLog.length}</span>
          </TabBtn>
          <TabBtn active={tab === 'vendors'} onClick={() => setTab('vendors')}>
            Vendors <span style={tabCountStyle}>{stageData.dataUploaded.vendorCount}</span>
          </TabBtn>
        </div>

        <div style={{ ...cardStyle, padding: '22px 26px', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
          {tab === 'info'    && <InfoTab detail={detail} />}
          {tab === 'audit'   && <AuditTab entries={auditLog} />}
          {tab === 'vendors' && <VendorsTab stageData={stageData} />}
        </div>
      </section>

      {tab === 'info' && tenant.customerStatus !== 'customer' && (
        <DangerZone
          tenantId={tenant.id}
          tenantName={tenant.name}
          onToast={onToast}
          onDeleted={onDeleted}
        />
      )}
    </>
  );
}

// ── Danger zone: hard-delete a never-promoted prospect ───────────────────────
function DangerZone({
  tenantId, tenantName, onToast, onDeleted,
}: {
  tenantId: string;
  tenantName: string;
  onToast: ToastFn;
  onDeleted: () => void;
}) {
  const [open, setOpen]       = useState(false);
  const [modal, setModal]     = useState(false);
  const [counts, setCounts]   = useState<Record<string, number> | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting]       = useState(false);
  const [err, setErr]                 = useState<string | null>(null);

  const nameMatches = confirmText.trim() === tenantName.trim();

  // On opening the modal, run a DRY RUN to show the exact blast radius.
  const openModal = async () => {
    setModal(true); setErr(null); setCounts(null); setConfirmText('');
    setLoading(true);
    try {
      const r = await superadminApi.hardDeleteProspect(tenantId, false);
      setCounts(r.counts);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Dry run failed');
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!nameMatches) return;
    setDeleting(true); setErr(null);
    try {
      const r = await superadminApi.hardDeleteProspect(tenantId, true);
      onToast('success', r.message ?? `Deleted ${tenantName}`);
      onDeleted();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Delete failed');
      setDeleting(false);
    }
  };

  return (
    <div style={{ marginTop: 28, border: '1px solid #fecaca', borderRadius: 12, background: '#fef2f2' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none',
                 padding: '14px 20px', cursor: 'pointer', color: '#b91c1c',
                 fontSize: 13, fontWeight: 600, fontFamily: SANS }}
      >
        {open ? '▾' : '▸'} Danger zone — permanently delete this prospect
      </button>

      {open && (
        <div style={{ padding: '0 20px 20px' }}>
          <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#7f1d1d', lineHeight: 1.5 }}>
            Hard-deletes <strong>{tenantName}</strong> and ALL its data
            (vendors, benchmarks, audit history). Irreversible. Only for
            prospects that never became customers — customers must be
            offboarded instead.
          </p>
          <button
            onClick={openModal}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ef4444',
                     background: '#fff', color: '#b91c1c', fontSize: 12.5,
                     fontWeight: 500, cursor: 'pointer', fontFamily: SANS }}
          >
            Delete prospect…
          </button>
        </div>
      )}

      {modal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1000,
                   background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
                   display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                   padding: '60px 20px', overflowY: 'auto' }}
          onClick={(e) => { if (e.target === e.currentTarget && !deleting) setModal(false); }}
        >
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
                         padding: '2rem', width: '100%', maxWidth: 480, fontFamily: SANS,
                         boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
            <p style={{ margin: 0, fontSize: '1rem', color: '#b91c1c', fontWeight: 600 }}>
              Permanently delete {tenantName}?
            </p>
            <p style={{ margin: '0.4rem 0 1rem', fontSize: 12.5, color: '#64748b' }}>
              This cannot be undone. Review exactly what will be deleted:
            </p>

            {loading && <p style={{ fontSize: 13, color: '#64748b' }}>Calculating blast radius…</p>}

            {counts && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0',
                            borderRadius: 8, padding: '10px 14px', marginBottom: 14,
                            fontSize: 12, fontFamily: MONO, color: '#334155' }}>
                {Object.entries(counts)
                  .filter(([, n]) => n !== 0)
                  .map(([tbl, n]) => (
                    <div key={tbl} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{tbl}</span><span>{n === -1 ? 'n/a' : n}</span>
                    </div>
                  ))}
                {Object.values(counts).every((n) => n === 0 || n === -1 || (counts.tenants === 1 && n === 1)) && (
                  <div style={{ color: '#94a3b8' }}>(tenant row + no child data)</div>
                )}
              </div>
            )}

            {err && (
              <div style={{ background: '#fef2f2', border: '1px solid rgba(239,68,68,0.3)',
                            borderRadius: 8, padding: '0.6rem 0.85rem', marginBottom: 14 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: '#b91c1c' }}>{err}</p>
              </div>
            )}

            <p style={{ margin: '0 0 6px', fontSize: 12, color: '#64748b' }}>
              Type the exact tenant name to confirm:
            </p>
            <p style={{ margin: '0 0 6px', fontSize: 13, fontFamily: MONO, color: '#0f172a' }}>{tenantName}</p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={deleting || loading}
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0',
                       borderRadius: 8, padding: '0.45rem 0.75rem', fontSize: 13,
                       fontFamily: SANS, outline: 'none', marginBottom: 16 }}
            />

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { if (!deleting) setModal(false); }}
                disabled={deleting}
                style={{ flex: 1, padding: '0.6rem', borderRadius: 8, border: '1px solid #e2e8f0',
                         background: 'none', color: '#64748b', fontSize: 13,
                         cursor: deleting ? 'default' : 'pointer', fontFamily: SANS }}
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                disabled={!nameMatches || deleting || loading}
                style={{ flex: 2, padding: '0.6rem', borderRadius: 8, border: 'none',
                         background: (!nameMatches || deleting || loading) ? '#fca5a5' : '#dc2626',
                         color: '#fff', fontSize: 13, fontWeight: 600,
                         cursor: (!nameMatches || deleting || loading) ? 'default' : 'pointer',
                         fontFamily: SANS }}
              >
                {deleting ? 'Deleting…' : 'Permanently delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Progress bar ─────────────────────────────────────────────────────────────
function StageProgressBar({
  completedStage, stageData,
}: {
  /** Highest stage completed (0 = nothing complete, 7 = all complete / live). */
  completedStage: number;
  stageData: TenantOnboardingDetail['stageData'];
}) {
  const isLive = completedStage >= 7;
  return (
    <div className="stage-bar">
      {STAGES.map((s, i) => {
        const isDone    = s.n <= completedStage;
        const isCurrent = !isLive && s.n === completedStage + 1;
        const completedAt = stageData[s.key].completedAt;

        return (
          <div key={s.key} className="stage-bar-item">
            {/* Connecting line to prev node (hidden on first) */}
            {i > 0 && (
              <div className="stage-bar-line" data-filled={isDone || (isCurrent && stageData[STAGES[i - 1].key].completedAt) ? 'true' : 'false'} />
            )}

            {/* Node */}
            <div
              className="stage-node"
              data-state={isDone ? 'done' : isCurrent ? 'current' : 'pending'}
              title={completedAt ? `Completed ${fmtDate(completedAt)}` : undefined}
            >
              {isDone ? (
                <CheckSvg />
              ) : isCurrent ? (
                <span className="stage-node-dot stage-node-dot--pulse" />
              ) : (
                <span className="stage-node-dot stage-node-dot--muted" />
              )}
            </div>

            {/* Label */}
            <div className="stage-bar-label">
              <div style={{
                fontSize: 11, fontWeight: 500,
                color: isDone ? C.primary : isCurrent ? C.textPrimary : C.textMuted,
                letterSpacing: 0.2, marginBottom: 2,
              }}>
                {String(s.n).padStart(2, '0')}
              </div>
              <div style={{
                fontSize: 11.5,
                color: isCurrent ? C.textPrimary : C.textSecondary,
                fontWeight: isCurrent ? 400 : 300, letterSpacing: '-0.1px',
                whiteSpace: 'nowrap',
              }}>
                {s.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Current stage card ───────────────────────────────────────────────────────
function CurrentStageCard({
  stage, stageData, tenantId, tenantName, customerStatus, onToast, onRefresh,
}: {
  stage: typeof STAGES[number];
  stageData: TenantOnboardingDetail['stageData'];
  tenantId: string;
  tenantName: string;
  customerStatus: string | null;
  onToast: ToastFn;
  onRefresh: () => Promise<void>;
}) {
  const vendorCount = (stageData.dataUploaded as { vendorCount: number }).vendorCount;
  const savingsMid  = Math.round((stageData.benchmarked.savingsLowUsd + stageData.benchmarked.savingsHighUsd) / 2) || null;

  return (
    <div style={currentCardStyle}>
      <div style={currentCardAccentStyle} />
      <div style={{ padding: '28px 32px 26px' }}>
        <div style={{
          fontSize: 10.5, fontWeight: 500, color: C.primary, letterSpacing: '0.12em',
          textTransform: 'uppercase', marginBottom: 8,
        }}>
          Current Stage
        </div>
        <h2 style={{
          margin: '0 0 8px', fontSize: 22, fontWeight: 400,
          color: C.textPrimary, letterSpacing: '-0.4px',
        }}>
          Stage {stage.n} — {stage.label}
        </h2>
        <p style={{
          margin: '0 0 22px', fontSize: 14, color: C.textSecondary,
          lineHeight: 1.65, fontWeight: 300, maxWidth: 680,
        }}>
          {stage.description}
        </p>

        {/* Inline stage-specific data preview */}
        {stage.key === 'dataUploaded' && vendorCount > 0 && (
          <StageFactRow facts={[
            { label: 'Vendors imported',   value: String(vendorCount) },
            { label: 'Currencies found',   value: stageData.dataUploaded.currenciesFound.join(', ') || '—' },
            { label: 'Total ACV (USD)',    value: fmtMoneyUsd(stageData.dataUploaded.totalAcvUsd) },
          ]} />
        )}
        {stage.key === 'benchmarked' && stageData.benchmarked.runId && (
          <StageFactRow facts={[
            { label: 'Assessed ACV',  value: fmtMoneyUsd(stageData.benchmarked.totalAssessedAcvUsd) },
            { label: 'Savings low',   value: fmtMoneyUsd(stageData.benchmarked.savingsLowUsd) },
            { label: 'Savings high',  value: fmtMoneyUsd(stageData.benchmarked.savingsHighUsd) },
          ]} />
        )}

        <StageAction
          stageKey={stage.key}
          customerStatus={customerStatus}
          tenantId={tenantId}
          tenantName={tenantName}
          vendorCount={vendorCount}
          savingsMidUsd={savingsMid}
          onToast={onToast}
          onRefresh={onRefresh}
        />
      </div>
    </div>
  );
}

// ── Stage action dispatcher ──────────────────────────────────────────────────
function StageAction({
  stageKey, tenantId, tenantName, customerStatus, vendorCount, savingsMidUsd, onToast, onRefresh,
}: {
  stageKey: StageKey;
  tenantId: string;
  tenantName: string;
  customerStatus: string | null;
  vendorCount: number;
  savingsMidUsd: number | null;
  onToast: ToastFn;
  onRefresh: () => Promise<void>;
}) {
  const [modal, setModal] = useState<null
    | 'excelSent' | 'upload' | 'runBenchmark'
    | 'proposalSent' | 'poReceived' | 'activatingConfirm' | 'promote'>(null);
  const [busy, setBusy] = useState(false);

  const markExcelSent = async () => {
    setBusy(true);
    try {
      await superadminApi.updateStage(tenantId, 2);
      onToast('success', 'Stage 1 marked complete — tenant moved to Data Uploaded.');
      await onRefresh();
    } catch (err) {
      onToast('error', err instanceof Error ? err.message : 'Failed to advance stage');
    } finally { setBusy(false); setModal(null); }
  };

  const markActivating = async () => {
    setBusy(true);
    try {
      await superadminApi.updateStage(tenantId, 7);
      onToast('success', 'Activation complete — tenant is now Live.');
      await onRefresh();
    } catch (err) {
      onToast('error', err instanceof Error ? err.message : 'Failed to mark activation complete');
    } finally { setBusy(false); setModal(null); }
  };

  if (stageKey === 'live') {
    return (
      <div style={liveCardStyle}>
        <div style={{ fontSize: 22, lineHeight: 1 }}>✅</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 400, color: '#065f46', marginBottom: 2 }}>Client is live in production</div>
          <div style={{ fontSize: 12, color: '#047857', fontWeight: 300 }}>Ongoing support and benchmarking refreshes begin now.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
      {stageKey === 'excelSent' && (
        <button style={actionBtnPrimary} disabled={busy} onClick={() => setModal('excelSent')}>
          Record Excel Sent →
        </button>
      )}

      {stageKey === 'dataUploaded' && (
        <button style={actionBtnPrimary} disabled={busy} onClick={() => setModal('upload')}>
          Upload Vendor Data →
        </button>
      )}

      {stageKey === 'benchmarked' && (
        <button
          style={vendorCount === 0 ? actionBtnDisabled : actionBtnPrimary}
          disabled={vendorCount === 0 || busy}
          onClick={() => setModal('runBenchmark')}
          title={vendorCount === 0 ? 'Upload vendor data first' : undefined}
        >
          Run Benchmarking →
        </button>
      )}

      {stageKey === 'proposalSent' && (
        <button style={actionBtnPrimary} disabled={busy} onClick={() => setModal('proposalSent')}>
          Mark Proposal Sent →
        </button>
      )}

      {stageKey === 'poReceived' && (
        <button style={actionBtnPrimary} disabled={busy} onClick={() => setModal('poReceived')}>
          Mark PO Received →
        </button>
      )}

      {stageKey === 'activating' && customerStatus === 'prospect' && (
        <button style={actionBtnPrimary} disabled={busy} onClick={() => setModal('promote')}>
          Promote to Customer →
        </button>
      )}

      {stageKey === 'activating' && customerStatus === 'customer' && (
        <div style={{ fontSize: 13, color: C.green, fontWeight: 500 }}>
          ✓ Promoted — engagement created, activation in progress
        </div>
      )}

      {/* Modals */}
      {modal === 'excelSent' && (
        <ConfirmModal
          title="Record Excel Sent?"
          body={<p style={modalBodyParaStyle}>Confirm the onboarding Excel has been sent to {tenantName}. This moves the tenant to <strong>Data Uploaded</strong>.</p>}
          confirmLabel="Mark Sent"
          busy={busy}
          onCancel={() => setModal(null)}
          onConfirm={markExcelSent}
        />
      )}

      {modal === 'upload' && (
        <UploadVendorDataModal
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={() => setModal(null)}
          onSuccess={async (r) => {
            onToast('success', `Imported ${r.rowsImported} vendor${r.rowsImported === 1 ? '' : 's'} for ${tenantName}.`);
            await onRefresh();
          }}
        />
      )}

      {modal === 'runBenchmark' && (
        <RunBenchmarkModal
          tenantId={tenantId}
          tenantName={tenantName}
          vendorCount={vendorCount}
          onClose={() => setModal(null)}
          onToast={onToast}
          onRefresh={onRefresh}
        />
      )}

      {modal === 'proposalSent' && (
        <MarkProposalSentModal
          tenantId={tenantId}
          defaultSavingsMidUsd={savingsMidUsd}
          onClose={() => setModal(null)}
          onToast={onToast}
          onRefresh={onRefresh}
        />
      )}

      {modal === 'poReceived' && (
        <MarkPoReceivedModal
          tenantId={tenantId}
          onClose={() => setModal(null)}
          onToast={onToast}
          onRefresh={onRefresh}
        />
      )}

      {modal === 'promote' && (
        <PromoteToCustomerModal
          tenantId={tenantId}
          tenantName={tenantName}
          prospectContactEmail={null}
          onClose={() => setModal(null)}
          onSuccess={(msg: string) => {
            setModal(null);
            onToast('success', msg);
            void onRefresh();
          }}
        />
      )}

      {modal === 'activatingConfirm' && (
        <ConfirmModal
          title="Mark activation complete?"
          body={<p style={modalBodyParaStyle}>SSO provisioning and production data migration are done. Moving {tenantName} to <strong>Live</strong>.</p>}
          confirmLabel="Mark Live"
          busy={busy}
          onCancel={() => setModal(null)}
          onConfirm={markActivating}
        />
      )}
    </div>
  );
}

// ── Run benchmarking modal (with progress polling) ───────────────────────────
function RunBenchmarkModal({
  tenantId, tenantName, vendorCount, onClose, onToast, onRefresh,
}: {
  tenantId: string;
  tenantName: string;
  vendorCount: number;
  onClose: () => void;
  onToast: ToastFn;
  onRefresh: () => Promise<void>;
}) {
  const [phase, setPhase] = useState<'confirm' | 'running' | 'done' | 'error'>('confirm');
  const [completed, setCompleted]   = useState(0);
  const [total, setTotal]           = useState(vendorCount);
  const [errMsg, setErrMsg]         = useState<string | null>(null);
  const [runId, setRunId]           = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (pollTimer.current) clearTimeout(pollTimer.current); }, []);

  const start = async () => {
    setPhase('running');
    try {
      const res = await superadminApi.runBenchmarking(tenantId);
      setRunId(res.runId);
      setTotal(res.vendorCount);
      pollStatus(res.runId);
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : 'Failed to start benchmarking');
      setPhase('error');
    }
  };

  const pollStatus = async (id: string) => {
    try {
      const s = await superadminApi.benchmarkStatus(tenantId, id);
      setCompleted(s.completedVendors);
      setTotal(s.totalVendors);
      if (s.status === 'complete') {
        setPhase('done');
        onToast('success', `Benchmarking complete — ${s.completedVendors}/${s.totalVendors} vendor${s.totalVendors === 1 ? '' : 's'} processed.`);
        await onRefresh();
        return;
      }
      if (s.status === 'failed') {
        setErrMsg(s.errorMessage ?? 'Benchmarking failed');
        setPhase('error');
        onToast('error', s.errorMessage ?? 'Benchmarking failed');
        return;
      }
      pollTimer.current = setTimeout(() => pollStatus(id), 3000);
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : 'Status check failed');
      setPhase('error');
    }
  };

  return (
    <ModalShell onClose={onClose} disabled={phase === 'running'}>
      <ModalHeader title={phase === 'done' ? 'Benchmarking complete' : 'Run benchmarking'} onClose={onClose} />
      {phase === 'confirm' && (
        <>
          <p style={modalBodyParaStyle}>
            Run Claude benchmarking for <strong>{vendorCount}</strong> vendor{vendorCount === 1 ? '' : 's'} across {tenantName}?
          </p>
          <p style={modalHelperStyle}>Roughly ~8 seconds per vendor. You can leave this dialog open to watch progress.</p>
          <ModalActions>
            <button style={btnGhost} onClick={onClose}>Cancel</button>
            <button style={actionBtnPrimary} onClick={start} disabled={vendorCount === 0}>Start benchmarking</button>
          </ModalActions>
        </>
      )}
      {phase === 'running' && (
        <>
          <p style={modalBodyParaStyle}>Benchmarking in progress…</p>
          <ProgressBar completed={completed} total={total} />
          <p style={{ ...modalHelperStyle, marginTop: 10 }}>
            {completed} of {total} vendor{total === 1 ? '' : 's'} scored · {total > 0 ? Math.round((completed / total) * 100) : 0}%
          </p>
        </>
      )}
      {phase === 'done' && (
        <>
          <p style={modalBodyParaStyle}>
            All done. <strong>{completed}</strong> of {total} vendor{total === 1 ? '' : 's'} scored. Tenant has advanced to stage 4.
          </p>
          <ModalActions>
            <button style={actionBtnPrimary} onClick={onClose}>Close</button>
          </ModalActions>
        </>
      )}
      {phase === 'error' && (
        <>
          <p style={{ ...modalBodyParaStyle, color: '#991b1b' }}>{errMsg}</p>
          <ModalActions>
            <button style={btnGhost} onClick={onClose}>Close</button>
            {runId && <button style={actionBtnPrimary} onClick={() => { setErrMsg(null); setPhase('running'); pollStatus(runId); }}>Retry polling</button>}
          </ModalActions>
        </>
      )}
    </ModalShell>
  );
}

function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  return (
    <div style={{ background: C.borderSoft, height: 8, borderRadius: 99, overflow: 'hidden', marginTop: 14 }}>
      <div style={{
        height: '100%', width: `${pct}%`, background: C.primary,
        borderRadius: 99, transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

// ── Mark Proposal Sent modal ─────────────────────────────────────────────────
function MarkProposalSentModal({
  tenantId, defaultSavingsMidUsd, onClose, onToast, onRefresh,
}: {
  tenantId: string;
  defaultSavingsMidUsd: number | null;
  onClose: () => void;
  onToast: ToastFn;
  onRefresh: () => Promise<void>;
}) {
  const [proposalAmount, setProposalAmount] = useState('');
  const [savingsOffered, setSavingsOffered] = useState(defaultSavingsMidUsd != null ? String(defaultSavingsMidUsd) : '');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<{ proposalAmount?: string; savingsOffered?: string; notes?: string; submit?: string }>({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const e: typeof errors = {};
    const pa = proposalAmount.trim() ? Number(proposalAmount) : null;
    const so = savingsOffered.trim() ? Number(savingsOffered) : null;
    if (pa != null && (!Number.isFinite(pa) || pa < 0)) e.proposalAmount = 'Must be a non-negative number';
    if (so != null && (!Number.isFinite(so) || so < 0)) e.savingsOffered = 'Must be a non-negative number';
    if (notes.length > 1000)                             e.notes          = 'Max 1000 characters';
    if (Object.keys(e).length) { setErrors(e); return; }

    setBusy(true); setErrors({});
    try {
      await superadminApi.markProposalSent(tenantId, {
        proposalAmount: pa, savingsOffered: so, notes: notes.trim() || null,
      });
      onToast('success', 'Proposal marked as sent. Tenant moved to PO Received.');
      await onRefresh();
      onClose();
    } catch (err) {
      setErrors({ submit: err instanceof Error ? err.message : 'Failed to mark proposal sent' });
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} disabled={busy}>
      <ModalHeader title="Mark proposal sent" onClose={onClose} />
      <div style={formFieldsStyle}>
        <Field label="Proposal amount (USD)" error={errors.proposalAmount}>
          <input type="number" min={0} step="0.01" value={proposalAmount}
            onChange={(e) => setProposalAmount(e.target.value)}
            placeholder="e.g. 42000" style={inputStyle(!!errors.proposalAmount)} />
        </Field>
        <Field label="Savings offered (USD)" error={errors.savingsOffered}>
          <input type="number" min={0} step="0.01" value={savingsOffered}
            onChange={(e) => setSavingsOffered(e.target.value)}
            placeholder="e.g. 12000" style={inputStyle(!!errors.savingsOffered)} />
          {defaultSavingsMidUsd != null && (
            <span style={helperTextStyle}>Pre-filled from benchmark midpoint ({fmtMoneyUsd(defaultSavingsMidUsd)})</span>
          )}
        </Field>
        <Field label="Notes (optional, max 1000 chars)" error={errors.notes}>
          <textarea rows={4} value={notes} maxLength={1000}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Attachments sent, next steps, stakeholder sign-off, etc."
            style={textareaStyle(!!errors.notes)} />
        </Field>
      </div>
      {errors.submit && <div style={submitErrorStyle}>{errors.submit}</div>}
      <ModalActions>
        <button style={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
        <button style={actionBtnPrimary} onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Mark as Sent'}</button>
      </ModalActions>
    </ModalShell>
  );
}

// ── Mark PO Received modal ───────────────────────────────────────────────────
function MarkPoReceivedModal({
  tenantId, onClose, onToast, onRefresh,
}: {
  tenantId: string;
  onClose: () => void;
  onToast: ToastFn;
  onRefresh: () => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate]     = useState(today);
  const [notes, setNotes]       = useState('');
  const [errors, setErrors]     = useState<{ poNumber?: string; poDate?: string; notes?: string; submit?: string }>({});
  const [busy, setBusy]         = useState(false);

  const submit = async () => {
    const e: typeof errors = {};
    if (!poNumber.trim())          e.poNumber = 'Required';
    if (poNumber.length > 100)     e.poNumber = 'Max 100 characters';
    if (!poDate.trim())            e.poDate   = 'Required';
    if (notes.length > 1000)       e.notes    = 'Max 1000 characters';
    if (Object.keys(e).length) { setErrors(e); return; }

    setBusy(true); setErrors({});
    try {
      await superadminApi.markPoReceived(tenantId, { poNumber: poNumber.trim(), poDate, notes: notes.trim() || null });
      onToast('success', `PO ${poNumber.trim()} recorded. Tenant moved to Activating.`);
      await onRefresh();
      onClose();
    } catch (err) {
      setErrors({ submit: err instanceof Error ? err.message : 'Failed to record PO' });
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} disabled={busy}>
      <ModalHeader title="Mark PO received" onClose={onClose} />
      <div style={formFieldsStyle}>
        <Field label="PO number *" error={errors.poNumber}>
          <input type="text" value={poNumber} maxLength={100}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="e.g. PO-2026-00412" style={inputStyle(!!errors.poNumber)} />
        </Field>
        <Field label="PO date *" error={errors.poDate}>
          <input type="date" value={poDate}
            onChange={(e) => setPoDate(e.target.value)}
            style={inputStyle(!!errors.poDate)} />
        </Field>
        <Field label="Notes (optional, max 1000 chars)" error={errors.notes}>
          <textarea rows={4} value={notes} maxLength={1000}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Payment terms, contact, special conditions…"
            style={textareaStyle(!!errors.notes)} />
        </Field>
      </div>
      {errors.submit && <div style={submitErrorStyle}>{errors.submit}</div>}
      <ModalActions>
        <button style={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
        <button style={actionBtnPrimary} onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Mark PO Received'}</button>
      </ModalActions>
    </ModalShell>
  );
}

// ── Confirm modal (shared) ───────────────────────────────────────────────────
function ConfirmModal({
  title, body, confirmLabel, busy, destructive, onCancel, onConfirm,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  busy?: boolean;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ModalShell onClose={onCancel} disabled={busy}>
      <ModalHeader title={title} onClose={onCancel} />
      <div style={{ padding: '4px 0 12px' }}>{body}</div>
      <ModalActions>
        <button style={btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          style={destructive ? btnDestructive : actionBtnPrimary}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </ModalActions>
    </ModalShell>
  );
}

// ── Modal primitives ─────────────────────────────────────────────────────────
function ModalShell({
  children, onClose, disabled,
}: { children: React.ReactNode; onClose: () => void; disabled?: boolean }) {
  // Body-scroll lock: prevent double-scroll of page behind modal while modal is open.
  // Saves the original inline overflow value so we don't clobber callers that
  // may have set their own.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      style={modalOverlayStyle}
      onClick={(e) => { if (!disabled && e.target === e.currentTarget) onClose(); }}
    >
      <div style={modalContainerStyle}>{children}</div>
    </div>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 400, color: C.textPrimary, letterSpacing: '-0.2px' }}>{title}</h3>
      <button onClick={onClose} style={{
        background: '#f1f5f9', border: 'none', borderRadius: 6,
        width: 26, height: 26, cursor: 'pointer', color: C.textMuted,
        fontSize: 15, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>×</button>
    </div>
  );
}

function ModalActions({ children }: { children: React.ReactNode }) {
  return (
    <div style={modalActionsStyle}>
      {children}
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={fieldLabelStyle}>{label}</label>
      {children}
      {error && <div style={fieldErrorStyle}>{error}</div>}
    </div>
  );
}

function StageFactRow({ facts }: { facts: Array<{ label: string; value: string }> }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `repeat(${facts.length}, minmax(0, 1fr))`,
      gap: 18, padding: '14px 0 22px',
      borderTop: `1px solid ${C.borderSoft}`,
      marginBottom: 16,
    }}>
      {facts.map((f) => (
        <div key={f.label}>
          <div style={dataLabelStyle}>{f.label}</div>
          <div style={dataValueStyle}>{f.value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Completed stage card ─────────────────────────────────────────────────────
function CompletedStageCard({
  stage, stageData, animationDelay, tenantId, onToast, onRefresh,
}: {
  stage: typeof STAGES[number];
  stageData: TenantOnboardingDetail['stageData'];
  animationDelay: number;
  tenantId: string;
  onToast: ToastFn;
  onRefresh: () => Promise<void>;
}) {
  const data    = stageData[stage.key];
  const summary = buildStageSummary(stage.key, stageData);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy]               = useState(false);

  const nextStage = stage.n + 1;
  const doRevert  = async () => {
    setBusy(true);
    try {
      await superadminApi.revertStage(tenantId, stage.n);
      onToast('success', `Reverted to stage ${stage.n} — ${stage.label}.`);
      setConfirmOpen(false);
      await onRefresh();
    } catch (err) {
      onToast('error', err instanceof Error ? err.message : 'Failed to revert stage');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="sl-stagger onb-completed"
      style={{ ...completedCardStyle, animationDelay: `${animationDelay}ms` }}
    >
      <div style={completedIconStyle}>
        <CheckSvg size={12} stroke={C.green} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: C.textMuted, fontFamily: MONO }}>{String(stage.n).padStart(2, '0')}</span>
          <span style={{ fontSize: 14, fontWeight: 400, color: C.textPrimary, letterSpacing: '-0.1px' }}>{stage.label}</span>
          <span style={{ fontSize: 11.5, color: C.textMuted, fontWeight: 300 }}>
            {data.completedAt ? `Completed ${fmtDate(data.completedAt)}` : 'Completed'}
          </span>
        </div>
        {summary && (
          <div style={{ marginTop: 4, fontSize: 12.5, color: C.textSecondary, fontWeight: 300, lineHeight: 1.5 }}>
            {summary}
          </div>
        )}
      </div>
      <button
        className="onb-redo-btn"
        onClick={() => setConfirmOpen(true)}
        disabled={busy}
        aria-label={`Redo stage ${stage.n}`}
      >
        ↺ Redo
      </button>
      {confirmOpen && (
        <ConfirmModal
          title={`Re-run stage ${stage.n}?`}
          body={
            <p style={modalBodyParaStyle}>
              This will clear data from stages <strong>{nextStage}</strong> onwards and reset the tenant to
              stage <strong>{stage.n} — {stage.label}</strong>. Audit history is preserved.
            </p>
          }
          confirmLabel={`Revert to stage ${stage.n}`}
          busy={busy}
          destructive
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doRevert}
        />
      )}
    </div>
  );
}

function buildStageSummary(key: StageKey, sd: TenantOnboardingDetail['stageData']): string | null {
  switch (key) {
    case 'excelSent':
      return sd.excelSent.notes ? sd.excelSent.notes : 'Excel template sent to portal admin';
    case 'dataUploaded': {
      const d = sd.dataUploaded;
      const parts: string[] = [];
      if (d.vendorCount > 0)           parts.push(`${d.vendorCount} vendor${d.vendorCount === 1 ? '' : 's'} imported`);
      if (d.currenciesFound.length)    parts.push(d.currenciesFound.join(', '));
      return parts.join(' · ') || null;
    }
    case 'benchmarked': {
      const d = sd.benchmarked;
      if (!d.runId) return null;
      const acv = fmtMoneyUsd(d.totalAssessedAcvUsd);
      const low = fmtMoneyUsd(d.savingsLowUsd);
      const high = fmtMoneyUsd(d.savingsHighUsd);
      return `Assessed ${acv} · ${low}–${high} savings`;
    }
    case 'proposalSent': {
      const d = sd.proposalSent;
      if (d.proposalAmount != null && d.savingsOffered != null) {
        return `Proposal ${fmtMoneyUsd(d.proposalAmount)} · ${fmtMoneyUsd(d.savingsOffered)} savings`;
      }
      return d.completedAt ? 'Proposal sent to client' : null;
    }
    case 'poReceived':
      return sd.poReceived.poNumber
        ? `PO #${sd.poReceived.poNumber}${sd.poReceived.poDate ? ` · ${fmtDate(sd.poReceived.poDate)}` : ''}`
        : sd.poReceived.completedAt ? 'Purchase order received' : null;
    case 'activating':
      return sd.activating.completedAt ? 'Provisioning complete' : null;
    case 'live':
      return sd.live.completedAt ? 'Tenant live in production' : null;
    default:
      return null;
  }
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
function TabBtn({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className="onb-tab" data-active={active ? 'true' : 'false'}>
      {children}
    </button>
  );
}

function InfoTab({ detail }: { detail: TenantOnboardingDetail }) {
  const t = detail.tenant;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '18px 32px' }}>
      <InfoRow label="Company name"        value={t.name} />
      <InfoRow label="Tenant slug"         value={t.slug} mono />
      <InfoRow label="Email domain"        value={t.emailDomain ?? '—'} mono />
      <InfoRow label="HQ country"          value={t.hqCountry ?? '—'} />
      <InfoRow label="Region"              value={t.region} />
      <InfoRow label="Reporting currency"  value={t.displayCurrency} />
      <InfoRow label="Onboarding stage"    value={`Stage ${t.onboardingStage} of 7`} />
      <InfoRow label="Created"             value={fmtDate(t.createdAt)} />
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={dataLabelStyle}>{label}</div>
      <div style={{ ...dataValueStyle, fontFamily: mono ? MONO : SANS, fontSize: mono ? 13 : 14 }}>{value}</div>
    </div>
  );
}

function AuditTab({ entries }: { entries: TenantOnboardingDetail['auditLog'] }) {
  if (entries.length === 0) {
    return <div style={emptyStateStyle}>No audit entries yet.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {entries.map((e, i) => (
        <div
          key={i}
          style={{
            display: 'grid', gridTemplateColumns: '170px 1fr 180px',
            gap: 16, padding: '12px 0',
            borderBottom: i === entries.length - 1 ? 'none' : `1px solid ${C.borderSoft}`,
            alignItems: 'baseline',
          }}
        >
          <span style={{ fontSize: 12, color: C.textMuted, fontFamily: MONO }}>
            {fmtDateTime(e.createdAt)}
          </span>
          <div>
            <div style={{ fontSize: 13, color: C.textPrimary, fontWeight: 400 }}>
              {humanAction(e.action)}
            </div>
            {e.metadata && typeof e.metadata === 'object' && Object.keys(e.metadata).length > 0 && (
              <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 2, fontFamily: MONO }}>
                {describeMetadata(e.metadata)}
              </div>
            )}
          </div>
          <span style={{ fontSize: 12, color: C.textSecondary, fontFamily: MONO, textAlign: 'right' }}>
            {e.actorEmail ?? '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

function describeMetadata(m: Record<string, unknown>): string {
  if ('from' in m && 'to' in m) return `${m.from} → ${m.to}`;
  const entries = Object.entries(m).slice(0, 3);
  return entries.map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(' · ');
}

function VendorsTab({ stageData }: { stageData: TenantOnboardingDetail['stageData'] }) {
  const d = stageData.dataUploaded;
  if (d.vendorCount === 0) {
    return <div style={emptyStateStyle}>No vendor data uploaded yet.</div>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 24 }}>
      <div>
        <div style={dataLabelStyle}>Vendors imported</div>
        <div style={{ ...dataValueStyle, fontSize: 26, fontWeight: 300 }}>{d.vendorCount}</div>
      </div>
      <div>
        <div style={dataLabelStyle}>Total ACV (USD)</div>
        <div style={{ ...dataValueStyle, fontSize: 26, fontWeight: 300 }}>{fmtMoneyUsd(d.totalAcvUsd)}</div>
      </div>
      <div>
        <div style={dataLabelStyle}>Currencies</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          {d.currenciesFound.length === 0
            ? <span style={{ fontSize: 13, color: C.textMuted }}>—</span>
            : d.currenciesFound.map((c) => (
                <span key={c} style={chipStyle}>{c}</span>
              ))
          }
        </div>
      </div>
    </div>
  );
}

// ── Skeleton loader ──────────────────────────────────────────────────────────
function PageSkeleton() {
  return (
    <div>
      <div className="sk-bar" style={{ width: 260, height: 28, marginBottom: 10 }} />
      <div className="sk-bar" style={{ width: 360, height: 14, marginBottom: 36 }} />
      <div className="sk-bar" style={{ width: '100%', height: 132, marginBottom: 24, borderRadius: 10 }} />
      <div className="sk-bar" style={{ width: '100%', height: 210, marginBottom: 32, borderRadius: 10 }} />
      <div className="sk-bar" style={{ width: '100%', height: 64, marginBottom: 10, borderRadius: 10 }} />
      <div className="sk-bar" style={{ width: '100%', height: 64, marginBottom: 10, borderRadius: 10 }} />
      <div className="sk-bar" style={{ width: '100%', height: 64,                  borderRadius: 10 }} />
    </div>
  );
}

// ── Error state ──────────────────────────────────────────────────────────────
function ErrorState({ title, message, action }: { title: string; message: string; action: React.ReactNode }) {
  return (
    <div style={{ ...cardStyle, padding: '48px 40px', textAlign: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 300, color: C.textPrimary, marginBottom: 8, letterSpacing: '-0.2px' }}>{title}</div>
      <p style={{ margin: '0 0 22px', fontSize: 13.5, color: C.textSecondary, fontWeight: 300 }}>{message}</p>
      {action}
    </div>
  );
}

// ── Small presentational pieces ──────────────────────────────────────────────
function InfoDot() {
  return <span style={{ width: 3, height: 3, borderRadius: '50%', background: C.border, flexShrink: 0 }} />;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10.5, fontWeight: 500, color: C.textMuted, letterSpacing: '0.12em',
      textTransform: 'uppercase', marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

function CheckSvg({ size = 14, stroke = '#ffffff' }: { size?: number; stroke?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const backLinkStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  fontSize: 12.5, color: C.textSecondary, fontWeight: 400,
  textDecoration: 'none', padding: '6px 12px', marginLeft: -12,
  borderRadius: 7, transition: 'color 0.15s, background 0.15s',
  fontFamily: SANS,
};

const pageTitleStyle: React.CSSProperties = {
  margin: 0, fontSize: 22, fontWeight: 300, color: C.textPrimary,
  letterSpacing: '-0.5px', fontFamily: SANS,
};

const subInfoRowStyle: React.CSSProperties = {
  marginTop: 8, display: 'flex', alignItems: 'center', gap: 10,
  flexWrap: 'wrap', color: C.textSecondary, fontSize: 12.5,
};

const chipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  padding: '2px 9px', borderRadius: 20,
  background: C.primarySoft, color: '#0369a1',
  fontSize: 11, fontWeight: 500, letterSpacing: '0.02em',
};

const cardStyle: React.CSSProperties = {
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const currentCardStyle: React.CSSProperties = {
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 10, boxShadow: '0 4px 14px rgba(14,165,233,0.08), 0 1px 3px rgba(0,0,0,0.04)',
  position: 'relative', overflow: 'hidden',
};

const currentCardAccentStyle: React.CSSProperties = {
  position: 'absolute', top: 0, left: 0, right: 0, height: 3,
  background: C.primary,
};

const completedCardStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 14,
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 10, padding: '14px 18px',
  opacity: 0, transform: 'translateY(6px)',
};

const completedIconStyle: React.CSSProperties = {
  flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
  background: C.greenSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
  marginTop: 1,
};

const upcomingRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14,
  padding: '8px 18px',
  borderRadius: 8,
};

const tabsBarStyle: React.CSSProperties = {
  display: 'flex', gap: 0, alignItems: 'flex-end',
  borderBottom: `1px solid ${C.border}`,
};

const tabCountStyle: React.CSSProperties = {
  display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 10,
  background: C.primarySoft, color: '#0369a1',
  fontSize: 10.5, fontWeight: 500,
};

const dataLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 500, color: C.textMuted,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  marginBottom: 5, fontFamily: SANS,
};

const dataValueStyle: React.CSSProperties = {
  fontSize: 14, fontWeight: 400, color: C.textPrimary, fontFamily: SANS,
};

const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-block', padding: '8px 18px', borderRadius: 8,
  background: C.primary, color: '#fff',
  fontSize: 13, fontWeight: 400, border: 'none', cursor: 'pointer',
  textDecoration: 'none', fontFamily: SANS,
  boxShadow: '0 1px 3px rgba(14,165,233,0.3)',
};

const actionBtnPrimary: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 8,
  background: C.primary, color: '#fff',
  fontSize: 12.5, fontWeight: 400, border: 'none', cursor: 'pointer',
  fontFamily: SANS, letterSpacing: '0.01em',
  boxShadow: '0 1px 3px rgba(14,165,233,0.3)',
  transition: 'background 0.15s ease, transform 0.1s ease',
};

const actionBtnDisabled: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 8,
  background: '#f1f5f9', color: C.textMuted,
  fontSize: 12.5, fontWeight: 400, border: `1px solid ${C.border}`,
  cursor: 'not-allowed', fontFamily: SANS,
};

const btnGhost: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 8,
  background: 'transparent', color: C.textSecondary,
  fontSize: 12.5, fontWeight: 400, border: `1px solid ${C.border}`,
  cursor: 'pointer', fontFamily: SANS,
};

const btnDestructive: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 8,
  background: '#dc2626', color: '#fff',
  fontSize: 12.5, fontWeight: 400, border: 'none', cursor: 'pointer',
  fontFamily: SANS,
  boxShadow: '0 1px 3px rgba(220,38,38,0.3)',
};

const liveCardStyle: React.CSSProperties = {
  marginTop: 10,
  display: 'flex', alignItems: 'center', gap: 14,
  padding: '16px 20px',
  background: C.greenSoft,
  border: '1px solid rgba(16,185,129,0.35)',
  borderRadius: 10,
  fontFamily: SANS,
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
  background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px 60px 20px',
  overflowY: 'auto',
};

const modalContainerStyle: React.CSSProperties = {
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 12, width: '100%', maxWidth: 480,
  maxHeight: 'calc(100vh - 120px)',
  overflowY: 'auto',
  padding: '22px 26px 0', fontFamily: SANS,
  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
  display: 'flex', flexDirection: 'column',
};

const modalActionsStyle: React.CSSProperties = {
  display: 'flex', gap: 8, justifyContent: 'flex-end',
  padding: '16px 26px 18px',
  marginTop: 18, marginLeft: -26, marginRight: -26,
  borderTop: `1px solid ${C.borderSoft}`,
  background: C.surface,
  position: 'sticky', bottom: 0,
  borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
};

const modalBodyParaStyle: React.CSSProperties = {
  margin: '0 0 10px', fontSize: 13.5, color: C.textSecondary,
  fontWeight: 300, lineHeight: 1.6,
};

const modalHelperStyle: React.CSSProperties = {
  margin: 0, fontSize: 12, color: C.textMuted, fontWeight: 300, lineHeight: 1.55,
};

const formFieldsStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 14,
  paddingTop: 4,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 500, color: C.textSecondary,
  letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6,
  fontFamily: SANS,
};

const fieldErrorStyle: React.CSSProperties = {
  marginTop: 5, fontSize: 11.5, color: '#b91c1c', fontWeight: 400,
};

const submitErrorStyle: React.CSSProperties = {
  marginTop: 14,
  background: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)',
  color: '#991b1b', borderRadius: 8, padding: '9px 12px',
  fontSize: 12.5, fontWeight: 400,
};

const helperTextStyle: React.CSSProperties = {
  display: 'inline-block', marginTop: 5, fontSize: 11.5, color: C.textMuted, fontWeight: 300,
};

const inputStyle = (hasError: boolean): React.CSSProperties => ({
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: `1px solid ${hasError ? '#ef4444' : C.border}`,
  background: C.surface, color: C.textPrimary,
  fontSize: 13.5, fontFamily: SANS, outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  boxSizing: 'border-box',
});

const textareaStyle = (hasError: boolean): React.CSSProperties => ({
  ...inputStyle(hasError), resize: 'vertical', minHeight: 86,
  lineHeight: 1.5,
});

const emptyStateStyle: React.CSSProperties = {
  padding: '28px 0', textAlign: 'center',
  fontSize: 13, color: C.textMuted, fontWeight: 300,
};

// ── CSS (animations, hover, responsive) ──────────────────────────────────────
const PAGE_CSS = `
  /* Back link hover */
  a[href="/superadmin"]:hover { color: ${C.textPrimary} !important; background: ${C.borderSoft}; }

  /* Fade-in on mount */
  .sl-fade { animation: sl-fade 0.4s ease-out both; }
  @keyframes sl-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

  /* Staggered entry for completed cards */
  .sl-stagger { animation: sl-slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }
  @keyframes sl-slide-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

  /* Stage progress bar layout */
  .stage-bar {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    align-items: start;
    gap: 0;
    position: relative;
  }
  .stage-bar-item {
    display: flex; flex-direction: column; align-items: center;
    position: relative; min-width: 0;
  }
  .stage-bar-line {
    position: absolute;
    top: 16px;
    right: 50%;
    width: calc(100% - 32px);
    height: 2px;
    background: ${C.border};
    transition: background 0.3s ease;
    z-index: 0;
  }
  .stage-bar-line[data-filled="true"] { background: ${C.primary}; }

  .stage-node {
    width: 32px; height: 32px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    position: relative; z-index: 1;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .stage-node[data-state="done"]    { background: ${C.primary}; }
  .stage-node[data-state="current"] { background: ${C.surface}; border: 2px solid ${C.primary}; box-shadow: 0 0 0 4px rgba(14,165,233,0.12); }
  .stage-node[data-state="pending"] { background: ${C.surface}; border: 2px solid ${C.border}; }
  .stage-node:hover { transform: scale(1.08); }

  .stage-node-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: ${C.primary};
  }
  .stage-node-dot--muted { background: ${C.textMuted}; opacity: 0.45; }
  .stage-node-dot--pulse { animation: sl-pulse-dot 1.6s ease-in-out infinite; }
  @keyframes sl-pulse-dot {
    0%, 100% { transform: scale(1); opacity: 1; }
    50%      { transform: scale(0.6); opacity: 0.4; }
  }

  .stage-bar-label {
    margin-top: 10px;
    text-align: center;
    padding: 0 4px;
  }

  /* Redo button on completed stages — hover reveal */
  .onb-completed { position: relative; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
  .onb-completed:hover { border-color: ${C.primary}; box-shadow: 0 2px 8px rgba(14,165,233,0.08); }
  .onb-redo-btn {
    opacity: 0; pointer-events: none;
    background: ${C.surface}; border: 1px solid ${C.border};
    color: ${C.textSecondary}; font-family: ${SANS};
    font-size: 11.5px; font-weight: 400; letter-spacing: 0.02em;
    padding: 5px 10px; border-radius: 6px; cursor: pointer;
    transition: opacity 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    align-self: center;
  }
  .onb-completed:hover .onb-redo-btn,
  .onb-completed:focus-within .onb-redo-btn {
    opacity: 1; pointer-events: auto;
  }
  .onb-redo-btn:hover { background: ${C.primarySoft}; color: #0369a1; border-color: ${C.primary}; }
  .onb-redo-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Action buttons */
  button:disabled { cursor: not-allowed; }

  /* Input focus */
  input:focus, textarea:focus, select:focus {
    border-color: ${C.primary} !important;
    box-shadow: 0 0 0 3px rgba(14,165,233,0.14);
  }

  /* Tab button */
  .onb-tab {
    background: none; border: none; cursor: pointer;
    padding: 10px 18px; font-size: 12.5px; font-weight: 400;
    color: ${C.textMuted}; font-family: ${SANS};
    border-bottom: 2px solid transparent; margin-bottom: -1px;
    transition: color 0.15s, border-color 0.15s;
    display: inline-flex; align-items: center;
  }
  .onb-tab:hover { color: ${C.textSecondary}; }
  .onb-tab[data-active="true"] {
    color: ${C.textPrimary};
    border-bottom-color: ${C.primary};
    font-weight: 500;
  }

  /* Skeleton loader */
  .sk-bar {
    background: linear-gradient(90deg, ${C.borderSoft} 0%, #ffffff 50%, ${C.borderSoft} 100%);
    background-size: 200% 100%;
    animation: sk-shimmer 1.6s ease-in-out infinite;
    border-radius: 6px;
  }
  @keyframes sk-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* Responsive: stage bar stacks vertically on narrow screens */
  @media (max-width: 720px) {
    .stage-bar {
      grid-template-columns: 1fr;
      gap: 4px;
    }
    .stage-bar-item {
      flex-direction: row;
      align-items: center;
      gap: 14px;
      padding: 6px 0;
    }
    .stage-bar-line {
      position: absolute;
      left: 15px; right: auto;
      top: auto; bottom: 100%;
      width: 2px; height: 12px;
    }
    .stage-bar-label {
      margin-top: 0;
      text-align: left;
    }
  }
`;
